import * as THREE from 'three';
import { panelSet, grateSet, hazardTexture, glowSprite } from './Textures.js';

/**
 * Rescale a BoxGeometry's UVs so texel density is constant no matter how big
 * the box is. One shared material can then cover a 2m crate and a 60m wall
 * without either looking stretched.
 */
export function applyBoxUV(geometry, w, h, d, tile = 4) {
  const uv = geometry.attributes.uv;
  if (!uv) return geometry;
  // BoxGeometry face order: +x, -x, +y, -y, +z, -z, four vertices each.
  const spans = [[d, h], [d, h], [w, d], [w, d], [w, h], [w, h]];
  for (let f = 0; f < 6; f++) {
    const su = spans[f][0] / tile;
    const sv = spans[f][1] / tile;
    for (let i = 0; i < 4; i++) {
      const idx = f * 4 + i;
      uv.setXY(idx, uv.getX(idx) * su, uv.getY(idx) * sv);
    }
  }
  uv.needsUpdate = true;
  return geometry;
}

const CYAN = 0x2ff3ff;
const MAGENTA = 0xff2fd0;
const AMBER = 0xffb020;
const LIME = 0x9dff3c;
const ORANGE = 0xff8a1f;

export class MaterialLibrary {
  constructor(tier) {
    this.tier = tier;
    this.cache = new Map();
    this.textures = [];
    this.sprites = {};
    this._build();
  }

  _track(set) {
    for (const t of Object.values(set)) if (t?.isTexture) this.textures.push(t);
    return set;
  }

  _build() {
    const size = this.tier.envRes >= 512 ? 512 : 256;
    const aniso = this.tier.anisotropy;

    const panelLit = this._track(panelSet({
      size, cells: 4, base: '#3a4a63', base2: '#2e3d54', seam: '#080d16',
      accent: '#2ff3ff', wear: 0.6, seed: 11,
    }));
    const panelWarm = this._track(panelSet({
      size, cells: 4, base: '#4a3f57', base2: '#3a3146', seam: '#0b0812',
      accent: '#ff2fd0', wear: 0.7, seed: 23,
    }));
    const plate = this._track(panelSet({
      size, cells: 2, base: '#232d40', base2: '#1a2231', seam: '#04060b',
      accent: null, wear: 0.9, seed: 47,
    }));
    const grate = this._track(grateSet({ size, bars: 14, seed: 61 }));

    for (const set of [panelLit, panelWarm, plate, grate]) {
      for (const t of Object.values(set)) if (t?.isTexture) t.anisotropy = aniso;
    }

    // --- portal ready surfaces. The emissive is deliberately dim: it only has
    // to read as "this one is different", and bloom multiplies whatever it is
    // given, so anything brighter turns the walls into light boxes.
    this.cache.set('panel', new THREE.MeshStandardMaterial({
      ...panelLit,
      color: 0xffffff,
      metalness: 0.10,
      roughness: 0.92,
      emissive: new THREE.Color(CYAN).multiplyScalar(0.6),
      emissiveIntensity: 0.85,
      envMapIntensity: 0.85,
    }));

    this.cache.set('panel_warm', new THREE.MeshStandardMaterial({
      ...panelWarm,
      color: 0xffffff,
      metalness: 0.10,
      roughness: 0.92,
      emissive: new THREE.Color(MAGENTA).multiplyScalar(0.6),
      emissiveIntensity: 0.78,
      envMapIntensity: 0.9,
    }));

    // --- inert structure
    this.cache.set('plate', new THREE.MeshStandardMaterial({
      ...plate,
      color: 0xffffff,
      metalness: 0.14,
      roughness: 0.82,
      envMapIntensity: 1.1,
    }));

    this.cache.set('grate', new THREE.MeshStandardMaterial({
      ...grate,
      color: 0xffffff,
      metalness: 0.35,
      roughness: 0.6,
      envMapIntensity: 1.3,
    }));

    this.cache.set('trim', new THREE.MeshStandardMaterial({
      color: 0x1b2430, metalness: 0.55, roughness: 0.4, envMapIntensity: 1.4,
    }));

    this.cache.set('hazard', new THREE.MeshStandardMaterial({
      map: hazardTexture({ size: 256 }),
      color: 0xffffff,
      metalness: 0.4, roughness: 0.7,
      emissive: new THREE.Color(MAGENTA), emissiveIntensity: 0.35,
    }));
    this.cache.get('hazard').map.repeat.set(4, 1);

    this.cache.set('glass', new THREE.MeshPhysicalMaterial({
      color: 0x0a1a26, metalness: 0.1, roughness: 0.06,
      transmission: 0, opacity: 0.35, transparent: true,
      envMapIntensity: 2.0,
    }));

    // --- pure emissive accents (bloom feeds on these)
    const glow = (hex, intensity = 1) => new THREE.MeshBasicMaterial({
      color: new THREE.Color(hex).multiplyScalar(intensity),
      toneMapped: false,
    });
    this.cache.set('glow_cyan', glow(CYAN, 1.05));
    this.cache.set('glow_magenta', glow(MAGENTA, 1.0));
    this.cache.set('glow_amber', glow(AMBER, 1.15));
    this.cache.set('glow_lime', glow(LIME, 1.0));
    this.cache.set('glow_orange', glow(ORANGE, 1.05));
    this.cache.set('glow_red', glow(0xff3355, 0.95));
    this.cache.set('glow_white', glow(0xd8f6ff, 0.85));

    this.cache.set('field_cyan', new THREE.MeshBasicMaterial({
      color: new THREE.Color(CYAN), transparent: true, opacity: 0.2,
      blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, toneMapped: false,
    }));
    this.cache.set('field_red', new THREE.MeshBasicMaterial({
      color: new THREE.Color(0xff3355), transparent: true, opacity: 0.28,
      blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, toneMapped: false,
    }));

    this.sprites.glow = glowSprite(128, 'rgba(255,255,255,1)');
  }

  get(name) {
    const m = this.cache.get(name);
    if (!m) {
      console.warn(`[materials] "${name}" not found, using plate`);
      return this.cache.get('plate');
    }
    return m;
  }

  has(name) { return this.cache.has(name); }

  /** Register a material at runtime; content packs use this. */
  register(name, material) { this.cache.set(name, material); return material; }

  dispose() {
    for (const m of this.cache.values()) m.dispose();
    for (const t of this.textures) t.dispose();
    for (const s of Object.values(this.sprites)) s.dispose?.();
    this.cache.clear();
  }
}

export const PALETTE = { CYAN, MAGENTA, AMBER, LIME, ORANGE };
